import { Injectable, Optional } from '@angular/core';
import { FhirApiService } from '../../services/api/fhir-api.sevice';

import { DataFhirService } from '../data-fhir.service';
import { HttpParamsObject } from '../../services/api/api.service';
import { UserService } from '../../services/user.service';
import { AuditEvent, NotificationsAdapter } from './notifications.model';
import { combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ReducerManager, select, Store } from '@ngrx/store';
import {
  isMarkAsReadSelector,
  messengerNotificationsSelector,
} from '../../store/selectors/notifications.selectors';
import { SetNotifications } from '../../store/actions/notifications.actions';
import { TranslocoService } from '@jsverse/transloco';
import { MessengerTranslationGroup } from '../../models/matrix-messenger/messenger.model';
import { UserInformationProvider } from '@xt/user-context';

// Messenger messages types
const MESSAGE_TYPES = {
  NEW_MESSAGE: 'xt.notification.new_message',
  NEW_ATTACHMENT: 'xt.notification.new_attachment',
  DIRECT_CHAT_INVITE: 'xt.notification.direct_chat_invite',
  GROUP_CHAT_INVITE: 'xt.notification.group_chat_invite',
  ADMIN_PERMISSION_GRANTED: 'xt.notification.admin_permission_granted',
  ADMIN_PERMISSION_REVOKED: 'xt.notification.admin_permission_revoked',
  MODERATOR_PERMISSION_GRANTED: 'xt.notification.moderator_permission_granted',
  MODERATOR_PERMISSION_REVOKED: 'xt.notification.moderator_permission_revoked',
  CHAT_RESTRICTED: 'xt.notification.chat_restricted',
  CHAT_REOPENED: 'xt.notification.chat_reopened',
};

@Injectable({ providedIn: 'root' })
export class NotificationsService extends DataFhirService<AuditEvent> {
  public readonly unreadNotifications$: Observable<AuditEvent[]>;
  public readonly unreadNotificationCount$: Observable<number>;
  public readonly hasUnreadNotifications$: Observable<boolean>;

  protected readonly defaultParamsObject: HttpParamsObject = {
    type: 'N',
    _sort: '-date',
  };

  constructor(
    protected adapter: NotificationsAdapter,
    protected fhirApi: FhirApiService,
    protected userService: UserService,
    private notificationsStore: Store,
    private translocoService: TranslocoService,
    protected store: Store,
    protected manager: ReducerManager,
    @Optional() private userInfoProvider: UserInformationProvider,
  ) {
    super('auditEvent', adapter, fhirApi, userService, store, manager);

    const notifications$ = this.notificationsStore.pipe(
      select(messengerNotificationsSelector),
    );

    const isMarkAsReadDelay$ = this.notificationsStore.pipe(
      select(isMarkAsReadSelector),
    );

    this.unreadNotifications$ = notifications$.pipe(
      map((entries) => entries.filter((entry) => !entry.read)),
    );
    this.unreadNotificationCount$ = this.unreadNotifications$.pipe(
      map((entries) => entries.length),
    );

    this.hasUnreadNotifications$ = combineLatest([
      this.unreadNotificationCount$,
      isMarkAsReadDelay$,
    ]).pipe(map(([count, isMarkAsRead]) => count > 0 && !isMarkAsRead));
  }

  fetchDataBasedOnContext() {
    if (!this.userInfoProvider) {
      console.warn('UserInformationProvider is not available');
      return;
    }
    this.userInfoProvider.context$.pipe(take(1)).subscribe((context) => {
      const person = context.person;

      switch (person._resourceType) {
        case 'Patient':
          this.fetchDataByPatientId(person.id);
          break;
        case 'Practitioner':
          this.fetchDataByPractitionerEntityId(person.id);
          break;
        case 'Person':
          this.fetchData({
            entity: `Person/${person.id}`,
          });
          break;
      }
    });
  }

  fetchData(paramsObject?: HttpParamsObject): void {
    this.setLoading(true);

    this.searchItems$(paramsObject).subscribe(
      (dataList) => {
        dataList = dataList.filter((x) => x !== undefined);
        this.currentFetchParams = paramsObject || {};
        this.notificationsStore.dispatch(
          SetNotifications({
            notifications: {
              list: dataList,
              type: 'main',
            },
          }),
        );
      },
      (err) => this.setError(err),
      () => this.setLoading(false),
    );
  }

  markAsReadById(id: string) {
    return this.getItemById$(id)
      .pipe(take(1))
      .subscribe((auditEvent) => {
        this.updateItem({
          ...auditEvent,
          _resource: {
            ...auditEvent._resource,
            action: 'R',
          },
        });
      });
  }

  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  NEW_MESSAGES = [
    {
      type: MESSAGE_TYPES.NEW_MESSAGE,
      directChat: true,
      groupedByHash: true,
      isMultiple: false,
      text: ({ personDisplayName }) =>
        this.translocoService.translate('messenger.new-direct-message', {
          personDisplayName,
        }),
    },
    {
      type: MESSAGE_TYPES.NEW_MESSAGE,
      directChat: true,
      groupedByHash: true,
      isMultiple: true,
      text: ({ personDisplayName, count }) =>
        this.translocoService.translate('messenger.new-direct-messages', {
          personDisplayName,
          count,
        }),
    },
    {
      type: MESSAGE_TYPES.NEW_MESSAGE,
      directChat: false,
      groupedByHash: true,
      isMultiple: false,
      text: ({ roomDisplayName }) =>
        this.translocoService.translate('messenger.new-group-message', {
          roomDisplayName,
        }),
    },
    {
      type: MESSAGE_TYPES.NEW_MESSAGE,
      directChat: false,
      groupedByHash: true,
      isMultiple: true,
      text: ({ roomDisplayName, count }) =>
        this.translocoService.translate('messenger.new-group-messages', {
          roomDisplayName,
          count,
        }),
    },
  ];

  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  NEW_ATTACHMENTS = [
    {
      type: MESSAGE_TYPES.NEW_ATTACHMENT,
      directChat: true,
      groupedByHash: true,
      isMultiple: false,
      text: ({ personDisplayName }) =>
        this.translocoService.translate('messenger.new-attachment', {
          personDisplayName,
        }),
    },
    {
      type: MESSAGE_TYPES.NEW_ATTACHMENT,
      directChat: true,
      groupedByHash: true,
      isMultiple: true,
      text: ({ personDisplayName, count }) =>
        this.translocoService.translate('messenger.new-attachments', {
          personDisplayName,
          count,
        }),
    },
    {
      type: MESSAGE_TYPES.NEW_ATTACHMENT,
      directChat: false,
      groupedByHash: true,
      isMultiple: false,
      text: ({ roomDisplayName }) =>
        this.translocoService.translate('messenger.new-group-attachment', {
          roomDisplayName,
        }),
    },
    {
      type: MESSAGE_TYPES.NEW_ATTACHMENT,
      directChat: false,
      groupedByHash: true,
      isMultiple: true,
      text: ({ roomDisplayName, count }) =>
        this.translocoService.translate('messenger.new-group-attachments', {
          count,
          roomDisplayName,
        }),
    },
  ];

  // Ungrouped notifications by hash
  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  DIRECT_CHAT_INVITE = [
    {
      type: MESSAGE_TYPES.DIRECT_CHAT_INVITE,
      directChat: true,
      groupedByHash: false,
      isMultiple: false,
      text: ({ personDisplayName }) =>
        this.translocoService.translate('messenger.you-invited-direct-chat', {
          personDisplayName,
        }),
    },
  ];

  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  GROUP_CHAT_INVITE = [
    {
      type: MESSAGE_TYPES.GROUP_CHAT_INVITE,
      directChat: false,
      groupedByHash: false,
      isMultiple: false,
      text: ({ personDisplayName, roomDisplayName }) =>
        this.translocoService.translate('messenger.you-invited-group-chat', {
          roomDisplayName,
          personDisplayName,
        }),
    },
  ];

  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ADMIN_PERMISSION_GRANTED = [
    {
      type: MESSAGE_TYPES.ADMIN_PERMISSION_GRANTED,
      directChat: false,
      groupedByHash: false,
      isMultiple: false,
      text: ({ roomDisplayName }) =>
        this.translocoService.translate('messenger.added-as-admin', {
          roomDisplayName,
        }),
    },
  ];

  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ADMIN_PERMISSION_REVOKED = [
    {
      type: MESSAGE_TYPES.ADMIN_PERMISSION_REVOKED,
      directChat: false,
      groupedByHash: false,
      isMultiple: false,
      text: ({ roomDisplayName }) =>
        this.translocoService.translate('messenger.removed-as-admin', {
          roomDisplayName,
        }),
    },
  ];

  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  MODERATOR_PERMISSION_GRANTED = [
    {
      type: MESSAGE_TYPES.MODERATOR_PERMISSION_GRANTED,
      directChat: false,
      groupedByHash: false,
      isMultiple: false,
      text: ({ roomDisplayName }) =>
        this.translocoService.translate('messenger.added-as-moderator', {
          roomDisplayName,
        }),
    },
  ];

  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  MODERATOR_PERMISSION_REVOKED = [
    {
      type: MESSAGE_TYPES.MODERATOR_PERMISSION_REVOKED,
      directChat: false,
      groupedByHash: false,
      isMultiple: false,
      text: ({ roomDisplayName }) =>
        this.translocoService.translate('messenger.removed-as-moderator', {
          roomDisplayName,
        }),
    },
  ];

  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  DIRECT_CHAT_RESTRICTED = [
    {
      type: MESSAGE_TYPES.CHAT_RESTRICTED,
      directChat: true,
      groupedByHash: false,
      isMultiple: false,
      text: ({ personDisplayName }) =>
        this.translocoService.translate(
          'messenger.your-conversation-restricted',
          { personDisplayName },
        ),
    },
  ];

  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  DIRECT_CHAT_REOPENED = [
    {
      type: MESSAGE_TYPES.CHAT_REOPENED,
      directChat: true,
      groupedByHash: false,
      isMultiple: false,
      text: ({ personDisplayName }) =>
        this.translocoService.translate(
          'messenger.your-conversation-reopened',
          { personDisplayName },
        ),
    },
  ];

  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  GROUP_CHAT_RESTRICTED = [
    {
      type: MESSAGE_TYPES.CHAT_RESTRICTED,
      directChat: false,
      groupedByHash: false,
      isMultiple: false,
      text: ({ roomDisplayName }) =>
        this.translocoService.translate(
          'messenger.your-group-conversation-restricted',
          { roomDisplayName },
        ),
    },
  ];

  // Disabled es-lint because it is used as constant
  // eslint-disable-next-line @typescript-eslint/naming-convention
  GROUP_CHAT_REOPENED = [
    {
      type: MESSAGE_TYPES.CHAT_REOPENED,
      directChat: false,
      groupedByHash: false,
      isMultiple: false,
      text: ({ roomDisplayName }) =>
        this.translocoService.translate(
          'messenger.your-group-conversation-reopened',
          { roomDisplayName },
        ),
    },
  ];
  getMessengerNotifications(): MessengerTranslationGroup[] {
    const MESSAGE_VARIATIONS = [
      ...this.NEW_MESSAGES,
      ...this.NEW_ATTACHMENTS,
      ...this.DIRECT_CHAT_INVITE,
      ...this.GROUP_CHAT_INVITE,
      ...this.ADMIN_PERMISSION_GRANTED,
      ...this.ADMIN_PERMISSION_REVOKED,
      ...this.MODERATOR_PERMISSION_GRANTED,
      ...this.MODERATOR_PERMISSION_REVOKED,
      ...this.DIRECT_CHAT_RESTRICTED,
      ...this.DIRECT_CHAT_REOPENED,
      ...this.GROUP_CHAT_RESTRICTED,
      ...this.GROUP_CHAT_REOPENED,
    ];

    return MESSAGE_VARIATIONS;
  }
}
